<script lang="ts">
export default {
  name: 'ResultsPage',
};
</script>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { DateTime } from 'luxon';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import Badge from '@/components/ui/Badge.vue';
import UISelect from '@/components/ui/UISelect.vue';
import UIInput from '@/components/ui/UIInput.vue';
import UILabel from '@/components/ui/UILabel.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Combobox from '@/components/v2/Combobox.vue';
import DatePickerInput from '@/components/v2/DatePickerInput.vue';
import {
  Clock,
  FileText,
  User,
  Loader,
  AlertCircle,
  History,
  BotIcon,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  RotateCw,
  Building2,
  Search,
  ArrowLeft,
  ArrowRight,
  CircleChevronLeft,
  CircleChevronRight,
} from '@lucide/vue';
import AppModal from '@/components/v2/AppModal.vue';
import type {
  AIPromptResultWithReviews,
  ModelInvocation,
  ModelInvocationStatus,
  BulkInvokeCounts,
  AIPromptMapping,
  ExtractionFailure,
  ExtendedResultsPageItem,
  PromptResultFilterOptions,
} from '@/types/ai';
import { PROMPT_VERSION_LATEST } from '@/types/ai';
import type { CoverageType } from '@/types/PolicyType';
import type { DocumentType } from '@/types/v2/Document';
import { aiService } from '@/services/AIService';
import { policyTypeService } from '@/services/PolicyTypeService';
import { documentService } from '@/services/v2/DocumentService';
import {
  useModelInvocation,
  useAggregateInvocationPolling,
  getInvocationStatusLabel as getStatusLabel,
  ACTIVE_INVOCATIONS_LIMIT,
} from '@/composables/useModelInvocation';
import ExtractionReviewShell from '@/components/document/ExtractionReviewShell.vue';
import ExternalPolicyTargetSummary from '@/components/v2/external_policy_upload/ExternalPolicyTargetSummary.vue';
import ReprocessModal from '@/components/document/ReprocessModal.vue';
import Tooltip from '@/components/v2/Tooltip.vue';

const route = useRoute();
const router = useRouter();

// State
const isLoading = ref(false);
const error = ref<string>('');
const promptResults = ref<AIPromptResultWithReviews[]>([]);
const activeInvocations = ref<ModelInvocation[]>([]);
const extractionFailures = ref<ExtractionFailure[]>([]);
const selectedResult = ref<AIPromptResultWithReviews | null>(null);
// Highlights the row you last opened after returning to the list (selectedResult is
// null on the list view, so it can't drive the highlight).
const lastViewedResultId = ref<number | null>(null);
// Allowed targets for the coverage-line add/recategorize pickers: the linked policy's
// uniquely-lined coverage types, falling back to the mapped prompt schema's templates.
// Comes from the merged endpoint; empty hides the structural line controls.

// A document-exception notification links here with the failure to look at: status and
// accountSearch seed the filters, failureId (a processing-queue id) marks its row.
const DEEP_LINK_QUERY_KEYS = ['status', 'accountSearch', 'failureId'] as const;
const queryValue = (key: string) => (typeof route.query[key] === 'string' ? (route.query[key] as string) : '');
const linkedFailureId = computed(() => Number(queryValue('failureId')) || null);

const statusFilterOptions = [
  { value: '', label: 'All' },
  { value: 'Completed', label: 'Completed' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Failed', label: 'Failed' },
];

// Filter state
const selectedPromptFilter = ref<string>('');
const selectedUserFilter = ref<string>('');
const selectedStatusFilter = ref<string>(
  statusFilterOptions.some((option) => option.value === queryValue('status')) ? queryValue('status') : '',
);
const accountNameSearch = ref<string>(queryValue('accountSearch')); // bound to the input (updates immediately)
const debouncedAccountSearch = ref<string>(queryValue('accountSearch')); // drives server-side search (updated after a pause)

// Server-side pagination state. The Completed and Failed status views each paginate their
// own dataset. Prev/Next drive whichever page ref belongs to the current status filter.
const PAGE_SIZE = 25;
const currentPage = ref(0); // zero-indexed — Completed / All view
const totalCount = ref(0);
const offset = computed(() => currentPage.value * PAGE_SIZE);

const failuresPage = ref(0); // zero-indexed — Failed view
const extractionFailuresTotalCount = ref(0);
const failuresOffset = computed(() => failuresPage.value * PAGE_SIZE);

const activePage = computed(() =>
  selectedStatusFilter.value === 'Failed' ? failuresPage.value : currentPage.value,
);
const activeTotalCount = computed(() =>
  selectedStatusFilter.value === 'Failed' ? extractionFailuresTotalCount.value : totalCount.value,
);
const hasPrevPage = computed(() => activePage.value > 0);
const hasNextPage = computed(() => (activePage.value + 1) * PAGE_SIZE < activeTotalCount.value);

// Dropdown options sourced from the server so they cover all results, not just this page.
const filterOptions = ref<PromptResultFilterOptions>({ promptNames: [], userEmails: [] });

// Polling state
const { pollInvocationStatus, stopPollingInvocation, stopAllPolling } = useModelInvocation();

// Per-invocation polling vs one aggregate refresh loop for large batches (CAC-5607).
const { inProgressInvocations, queuedCount, runningCount, isAggregatePolling, syncPollingMode } =
  useAggregateInvocationPolling({
    activeInvocations,
    onFinished: () => Promise.all([loadResults(), loadExtractionFailures()]).then(() => undefined),
    startIndividualPoll: (invocationId) => startPollingInvocation(invocationId),
    stopAllIndividualPolls: () => stopAllPolling(),
  });

// Field data composable

// Reprocess modal
const reprocessModalRef = ref<InstanceType<typeof ReprocessModal> | null>(null);

// Bulk apply state
const showBulkApplyModal = ref(false);
const bulkApplyLoading = ref(false);
const bulkApplySubmitting = ref(false);
const showValidatedConfirmation = ref(false);

// Bulk apply form data
const coverageTypes = ref<CoverageType[]>([]);
const documentTypes = ref<DocumentType[]>([]);
const allPrompts = ref<{ promptId: number; name: string; versions: string[] }[]>([]);
const promptMappings = ref<AIPromptMapping[]>([]);

const selectedCoverageTypeId = ref('');
const selectedDocumentTypeId = ref('');
const selectedPromptId = ref('');
const selectedPromptVersion = ref('');
const includeUnprocessed = ref(true);
const includeUnvalidated = ref(true);
const includeExceptions = ref(false);
const includeValidated = ref(false);
const maxDocuments = ref(10);
const effectiveDateFrom = ref('');
const effectiveDateTo = ref('');
const bulkCounts = ref<BulkInvokeCounts | null>(null);
const bulkAutoFilled = ref(false);

// Computed - unique prompts for filter dropdown (server-sourced, covers all pages)
const uniquePrompts = computed(() => [
  { value: '', label: 'All Prompts' },
  ...filterOptions.value.promptNames.map((name) => ({ value: name, label: name })),
]);

// Computed - unique users for filter dropdown (server-sourced, covers all pages)
const uniqueUsers = computed(() => [
  { value: '', label: 'All Users' },
  ...filterOptions.value.userEmails.map((email) => ({ value: email, label: email })),
]);

// Type guards
const isInvocation = (item: ExtendedResultsPageItem): item is ModelInvocation => {
  return 'invocationId' in item && 'status' in item;
};

const isPromptResult = (item: ExtendedResultsPageItem): item is AIPromptResultWithReviews => {
  return 'id' in item && 'rawOutput' in item;
};

const isExtractionFailure = (item: ExtendedResultsPageItem): item is ExtractionFailure => {
  return 'source' in item;
};

// Account/prompt/user filtering and pagination are server-side for both completed results
// and extraction failures. In-progress items are still small enough to load fully. The
// status filter is client-side because it picks which paginated data source to render.
// inProgressInvocations/queuedCount/runningCount come from useAggregateInvocationPolling.
const failedItems = computed<ExtendedResultsPageItem[]>(() => [
  ...activeInvocations.value.filter((inv) => inv.status === 'FAILED'),
  ...extractionFailures.value,
]);

// Whether the current view paginates against a server-side dataset.
const showPagination = computed(
  () =>
    selectedStatusFilter.value === '' ||
    selectedStatusFilter.value === 'Completed' ||
    selectedStatusFilter.value === 'Failed',
);

// Computed - the items shown for the current status view + page, sorted by created date desc.
const sortedResults = computed(() => {
  let items: ExtendedResultsPageItem[];

  if (selectedStatusFilter.value === 'In Progress') {
    items = [...inProgressInvocations.value];
  } else if (selectedStatusFilter.value === 'Failed') {
    // Server-paginated. extractionFailures.value holds only the current page.
    items = [...failedItems.value];
  } else if (selectedStatusFilter.value === 'Completed') {
    items = [...promptResults.value];
  } else {
    // 'All': in-progress is bounded and stays as a live-status band on page 1. Failures
    // are paginated separately in the Failed tab and are NOT surfaced here.
    items = [...promptResults.value];
    if (currentPage.value === 0) {
      items.push(...inProgressInvocations.value);
    }
  }

  return items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
});

// Gate the filter bar / search box on there being data or an active filter.
const hasActiveFilters = computed(
  () =>
    !!accountNameSearch.value ||
    !!selectedPromptFilter.value ||
    !!selectedUserFilter.value ||
    !!selectedStatusFilter.value,
);
const hasAnyResults = computed(
  () =>
    totalCount.value > 0 ||
    activeInvocations.value.length > 0 ||
    extractionFailuresTotalCount.value > 0,
);

// Header badge count: server-side total for paginated views, else the transient view's size.
const displayCount = computed(() => {
  if (selectedStatusFilter.value === 'Failed') return extractionFailuresTotalCount.value;
  if (showPagination.value) return totalCount.value;
  return sortedResults.value.length;
});

// Bulk apply computed properties
const selectedBucketTotal = computed(() => {
  if (!bulkCounts.value) return 0;
  let total = 0;
  if (includeUnprocessed.value) total += bulkCounts.value.unprocessed;
  if (includeUnvalidated.value) total += bulkCounts.value.unvalidated;
  if (includeExceptions.value) total += bulkCounts.value.exceptions;
  if (includeValidated.value) total += bulkCounts.value.validated;
  return total;
});

const bulkApplyCount = computed(() => Math.min(selectedBucketTotal.value, maxDocuments.value));

const isDateRangeInverted = computed(() => {
  if (!effectiveDateFrom.value || !effectiveDateTo.value) return false;
  return effectiveDateFrom.value > effectiveDateTo.value;
});

const canSubmitBulkApply = computed(() => {
  return (
    selectedCoverageTypeId.value !== '' &&
    selectedDocumentTypeId.value !== '' &&
    selectedPromptId.value !== '' &&
    selectedPromptVersion.value !== '' &&
    (includeUnprocessed.value || includeUnvalidated.value || includeExceptions.value || includeValidated.value) &&
    bulkApplyCount.value > 0 &&
    !bulkApplySubmitting.value &&
    !isDateRangeInverted.value
  );
});

const coverageTypeOptions = computed(() => {
  return coverageTypes.value.map((ct) => ({ value: String(ct.coverageTypeId), label: ct.coverageType }));
});

const policyDocumentTypes = computed(() => documentTypes.value.filter((dt) => dt.domainName === 'Policy'));

const documentTypeOptions = computed(() => {
  return policyDocumentTypes.value.map((dt) => ({ value: String(dt.id), label: dt.name }));
});

const promptOptions = computed(() => {
  return allPrompts.value.map((p) => ({ value: String(p.promptId), label: p.name }));
});

const versionOptions = computed(() => {
  const prompt = allPrompts.value.find((p) => String(p.promptId) === selectedPromptId.value);
  if (!prompt) return [];
  return [
    { value: PROMPT_VERSION_LATEST, label: 'Latest' },
    ...prompt.versions.map((v) => ({ value: v, label: `v${v}` })),
  ];
});

// Lifecycle
onMounted(async () => {
  await loadResults();
  await loadFilterOptions();
  await loadActiveInvocations();
  await loadExtractionFailures();
  scrollToLinkedFailure();
  await loadBulkApplyData();

  // Check for resultId query param and auto-select the result
  const resultIdParam = route.query.resultId as string;
  if (resultIdParam) {
    const id = parseInt(resultIdParam, 10);
    if (!isNaN(id)) {
      const match = promptResults.value.find((r) => r.id === id);
      if (match) {
        lastViewedResultId.value = match.id;
        selectedResult.value = match;
      }
    }
  }
});

onUnmounted(() => {
  stopAllPolling();
});

// Sync selectedResult to URL query params
watch(selectedResult, (newResult) => {
  const query = { ...route.query };
  if (newResult) {
    const idStr = String(newResult.id);
    if (query.resultId !== idStr) {
      query.resultId = idStr;
      router.replace({ query });
    }
  } else if (query.resultId) {
    delete query.resultId;
    router.replace({ query });
  }
});

// Watch for route query changes to handle resultId from browser back/forward
watch(
  () => route.query.resultId,
  (newResultId) => {
    if (newResultId && typeof newResultId === 'string') {
      const id = parseInt(newResultId, 10);
      if (!isNaN(id) && selectedResult.value?.id !== id) {
        const match = promptResults.value.find((r) => r.id === id);
        if (match) {
          selectedResult.value = match;
        }
      }
    } else if (!newResultId && selectedResult.value) {
      selectedResult.value = null;
    }
  },
);

// Watch active invocations to auto-start/stop polling
watch(
  activeInvocations,
  (newInvocations, oldInvocations) => {
    // Aggregate mode owns refresh for large batches — no per-invocation polls
    if (isAggregatePolling()) return;
    // Start polling for new invocations that need it
    if (newInvocations && Array.isArray(newInvocations)) {
      newInvocations.forEach((inv) => {
        if (inv.status === 'PENDING' || inv.status === 'RUNNING') {
          // Check if this is a new invocation or status changed to PENDING/RUNNING
          const oldInv = oldInvocations?.find((old) => old.invocationId === inv.invocationId);
          if (!oldInv || (oldInv.status !== 'PENDING' && oldInv.status !== 'RUNNING')) {
            startPollingInvocation(inv.invocationId);
          }
        }
      });
    }
  },
  { deep: true },
);

// Functions
const loadResults = async () => {
  isLoading.value = true;
  error.value = '';

  try {
    const response = await aiService.getPromptResultsWithReviews({
      pageSize: PAGE_SIZE,
      pageOffset: offset.value,
      accountSearch: debouncedAccountSearch.value.trim() || undefined,
      promptName: selectedPromptFilter.value || undefined,
      userEmail: selectedUserFilter.value || undefined,
    });
    promptResults.value = response.results ?? [];
    totalCount.value = response.totalCount ?? 0;
  } catch (err: any) {
    error.value = err.message || 'Failed to load results';
    promptResults.value = [];
    totalCount.value = 0;
    console.error('Failed to load results:', err);
  } finally {
    isLoading.value = false;
  }
};

const loadFilterOptions = async () => {
  try {
    filterOptions.value = await aiService.getPromptResultFilterOptions();
  } catch (err) {
    console.error('Failed to load filter options:', err);
  }
};

const goToPreviousPage = () => {
  if (!hasPrevPage.value) return;
  if (selectedStatusFilter.value === 'Failed') failuresPage.value -= 1;
  else currentPage.value -= 1;
};

const goToNextPage = () => {
  if (!hasNextPage.value) return;
  if (selectedStatusFilter.value === 'Failed') failuresPage.value += 1;
  else currentPage.value += 1;
};

// Reload whenever the page changes (Previous/Next).
watch(currentPage, () => {
  loadResults();
});

watch(failuresPage, () => {
  loadExtractionFailures();
});

const loadActiveInvocations = async () => {
  try {
    const invocations = await aiService.getActiveInvocations(ACTIVE_INVOCATIONS_LIMIT);
    activeInvocations.value = invocations || [];
    // Sole owner of the polling strategy: aggregate loop for large batches, per-invocation
    // polls otherwise.
    syncPollingMode();
  } catch (err: any) {
    console.error('Failed to load active invocations:', err);
    activeInvocations.value = [];
  }
};

const loadExtractionFailures = async () => {
  try {
    const response = await aiService.getExtractionFailures({
      limit: PAGE_SIZE,
      offset: failuresOffset.value,
      accountSearch: debouncedAccountSearch.value.trim() || undefined,
      promptName: selectedPromptFilter.value || undefined,
      userEmail: selectedUserFilter.value || undefined,
    });
    extractionFailures.value = response.items;
    extractionFailuresTotalCount.value = response.totalCount;
  } catch (err: any) {
    console.error('Failed to load extraction failures:', err);
    extractionFailures.value = [];
    extractionFailuresTotalCount.value = 0;
  }
};

const startPollingInvocation = (invocationId: string) => {
  pollInvocationStatus(
    invocationId,
    async (statusResponse) => {
      if (statusResponse.status === 'COMPLETED') {
        // Stop polling
        stopPollingInvocation(invocationId);

        // Remove from active invocations
        if (activeInvocations.value) {
          activeInvocations.value = activeInvocations.value.filter((inv) => inv.invocationId !== invocationId);
        }

        // Fetch the result and add to promptResults
        try {
          const result = await aiService.getInvocationResult(invocationId);
          // Add default review values since this is a new result
          const resultWithReviews: AIPromptResultWithReviews = {
            ...result,
            reviews: [],
            reviewCount: 0,
          };
          promptResults.value.unshift(resultWithReviews);
        } catch (err) {
          console.error('Failed to get invocation result:', err);
        }
      } else if (statusResponse.status === 'FAILED') {
        // Stop polling
        stopPollingInvocation(invocationId);

        // Update the invocation in the list
        if (activeInvocations.value) {
          const invIndex = activeInvocations.value.findIndex((inv) => inv.invocationId === invocationId);
          if (invIndex !== -1) {
            activeInvocations.value[invIndex].status = 'FAILED';
            if (statusResponse.errorMessage) {
              activeInvocations.value[invIndex].errorMessage = statusResponse.errorMessage;
            }
          }
        }
      }
    },
    (error) => {
      console.error('Polling error:', error);
      // Continue polling on error
    },
    (statusResponse) => {
      // Update status in active invocations
      if (activeInvocations.value) {
        const invIndex = activeInvocations.value.findIndex((inv) => inv.invocationId === invocationId);
        if (invIndex !== -1) {
          activeInvocations.value[invIndex].status = statusResponse.status;
          if (statusResponse.errorMessage) {
            activeInvocations.value[invIndex].errorMessage = statusResponse.errorMessage;
          }
        }
      }
    },
  );
};

const formatDate = (dateString: string) => {
  return DateTime.fromISO(dateString).toLocaleString(DateTime.DATETIME_MED);
};

const getModelDisplayName = (modelArn: string) => {
  const parts = modelArn.split('/');
  return parts[parts.length - 1] || modelArn;
};

// Open a result's detail view. Extraction failures and in-progress invocations
// have no detail to show. A completed invocation maps to its prompt result.
const openResult = (item: ExtendedResultsPageItem) => {
  let result: AIPromptResultWithReviews | undefined;

  if (isInvocation(item)) {
    if (item.status === 'COMPLETED' && item.promptResultId) {
      result = promptResults.value.find((r) => r.id === item.promptResultId);
    }
  } else if (isPromptResult(item)) {
    result = item;
  }

  if (!result) return;
  lastViewedResultId.value = result.id;
  selectedResult.value = result;
};

// Return from the detail view to the list (the watcher clears the document/fields
// and the URL sync removes ?resultId). The list keeps its scroll position because
// it stays mounted (v-show), and lastViewedResultId re-highlights the opened row.
const handleBackToList = () => {
  selectedResult.value = null;
};

const canOpenResult = (item: ExtendedResultsPageItem): boolean => {
  if (isExtractionFailure(item)) return false;
  if (isInvocation(item)) return item.status === 'COMPLETED' && !!item.promptResultId;
  return isPromptResult(item);
};

const isLinkedFailure = (item: ExtendedResultsPageItem) =>
  isExtractionFailure(item) && item.source === 'PROCESSING_QUEUE' && item.id === linkedFailureId.value;

const resultsListEl = ref<HTMLElement | null>(null);
const scrollToLinkedFailure = async () => {
  if (linkedFailureId.value == null) return;
  await nextTick();
  resultsListEl.value?.querySelector('[data-linked-row]')?.scrollIntoView({ block: 'nearest' });
};

// The deep link only seeds the view. Once the admin changes it, a reload should keep their
// view rather than snap back to the link's.
const dropDeepLinkQuery = () => {
  if (!DEEP_LINK_QUERY_KEYS.some((key) => key in route.query)) return;
  const query = { ...route.query };
  for (const key of DEEP_LINK_QUERY_KEYS) delete query[key];
  router.replace({ query });
};

const isLastViewed = (item: ExtendedResultsPageItem) => {
  if (lastViewedResultId.value == null) return false;
  if (isExtractionFailure(item)) return false;
  if (isInvocation(item)) {
    return item.status === 'COMPLETED' && item.promptResultId === lastViewedResultId.value;
  }
  return isPromptResult(item) && item.id === lastViewedResultId.value;
};

const getItemKey = (item: ExtendedResultsPageItem): string => {
  if (isExtractionFailure(item)) {
    return `failure-${item.source}-${item.id}`;
  }
  if (isInvocation(item)) {
    return `invocation-${item.invocationId}`;
  }
  return `result-${item.id}`;
};

const getStatusVariant = (status: ModelInvocationStatus): 'outline' | 'destructive' => {
  switch (status) {
    case 'PENDING':
      return 'outline';
    case 'RUNNING':
      return 'outline';
    case 'COMPLETED':
      return 'outline';
    case 'FAILED':
      return 'destructive';
    default:
      return 'outline';
  }
};

const getStatusBadgeClass = (status: ModelInvocationStatus): string => {
  switch (status) {
    case 'PENDING':
      return 'border-yellow-200 bg-yellow-50 text-yellow-700';
    case 'RUNNING':
      return 'border-blue-200 bg-blue-50 text-blue-700';
    case 'COMPLETED':
      return 'border-green-200 bg-green-50 text-green-700';
    case 'FAILED':
      return 'border-red-200 bg-red-50 text-red-700';
    default:
      return '';
  }
};

// An external-designated prompt creates a policy through the binder flow. Prompt results,
// in-flight invocations and failures all carry the designation -- it comes off the prompt,
// so a binder is marked External while it is still scanning and if it never finishes.
const isExternalResult = (item: ExtendedResultsPageItem | AIPromptResultWithReviews | null) => {
  if (!item) return false;
  return 'designation' in item && item.designation === 'external';
};

const handleReviewCompleted = async () => {
  // Refresh results to show updated review info
  await loadResults();
  // Also refresh the selected result's data if still selected
  if (selectedResult.value) {
    const updatedResult = promptResults.value.find((r) => r.id === selectedResult.value?.id);
    if (updatedResult) {
      selectedResult.value = updatedResult;
    }
  }
};

const formatReviewDate = (dateString: string) => {
  return DateTime.fromISO(dateString).toRelative();
};

const navigateToPrompt = (promptName: string, event: Event) => {
  event.stopPropagation();
  router.push({
    path: '/admin/ai',
    query: { tab: 'prompts', prompt: promptName },
  });
};

// Precompute coverage-type name keyed by `${promptId}:${promptVersion}` so each row's
// account chip is an O(1) lookup instead of scanning mappings/docTypes/coverageTypes
// on every render (called per row, twice, across ~1200 rows).
const covNameByPromptKey = computed(() => {
  const docTypeById = new Map(documentTypes.value.map((dt) => [dt.id, dt]));
  const covTypeById = new Map(coverageTypes.value.map((c) => [c.coverageTypeId, c]));
  const map = new Map<string, string>();
  for (const mapping of promptMappings.value) {
    const docType = docTypeById.get(mapping.documentTypeId);
    if (docType?.domainName === 'Policy') {
      const ct = covTypeById.get(mapping.coverageTypeId);
      if (ct) map.set(`${mapping.promptId}:${mapping.promptVersion}`, ct.coverageType);
    }
  }
  return map;
});

const getAccountChipInfo = (result: AIPromptResultWithReviews): { accountId: number; accountName: string; covName?: string } | null => {
  if (!result.documentAccountId || !result.documentAccountName) return null;

  const info: { accountId: number; accountName: string; covName?: string } = {
    accountId: result.documentAccountId,
    accountName: result.documentAccountName,
  };

  const covName = covNameByPromptKey.value.get(`${result.promptId}:${result.promptVersion}`);
  if (covName) info.covName = covName;
  return info;
};

const goToAccountPolicies = (result: AIPromptResultWithReviews, event: Event) => {
  event.stopPropagation();
  const info = getAccountChipInfo(result);
  if (!info) return;
  const query: Record<string, string> = {};
  if (info.covName) query.covName = info.covName;
  router.push({ path: `/account/${info.accountId}/policy-detail`, query });
};

const handleReprocess = (item: AIPromptResultWithReviews, event: Event) => {
  event.stopPropagation();
  reprocessModalRef.value?.show(item);
};

const handleReprocessed = async () => {
  await loadActiveInvocations();
};

// Bulk apply methods
async function loadBulkApplyData() {
  try {
    const [ctypes, dtypes, prompts, mappings] = await Promise.all([
      policyTypeService.GetDistinctCoverageTypes(),
      documentService.getDocumentTypes(),
      aiService.getAllPrompts('internal'),
      aiService.getPromptMappings(),
    ]);
    coverageTypes.value = ctypes;
    documentTypes.value = dtypes;
    promptMappings.value = mappings;

    // Group prompts by promptId, collecting published versions
    const promptMap = new Map<number, { promptId: number; name: string; versions: string[] }>();
    for (const p of prompts) {
      const id = typeof p.promptId === 'string' ? parseInt(p.promptId) : p.promptId;
      if (p.isDraft) continue;
      if (!promptMap.has(id)) {
        promptMap.set(id, { promptId: id, name: p.name, versions: [] });
      }
      promptMap.get(id)!.versions.push(p.version);
    }
    allPrompts.value = Array.from(promptMap.values());
  } catch (e) {
    console.error('Failed to load bulk apply data', e);
  }
}

async function fetchBulkCounts() {
  if (!selectedCoverageTypeId.value || !selectedDocumentTypeId.value) {
    bulkCounts.value = null;
    return;
  }
  if (isDateRangeInverted.value) {
    // Leave the last-known counts visible to avoid flicker; the inline warning
    // already tells the user why no refetch happened.
    return;
  }
  bulkApplyLoading.value = true;
  try {
    bulkCounts.value = await aiService.getBulkInvokeCounts(
      Number(selectedCoverageTypeId.value),
      Number(selectedDocumentTypeId.value),
      {
        effectiveDateFrom: effectiveDateFrom.value || undefined,
        effectiveDateTo: effectiveDateTo.value || undefined,
      },
    );
  } catch (e) {
    console.error('Failed to fetch bulk counts', e);
    bulkCounts.value = null;
  } finally {
    bulkApplyLoading.value = false;
  }
}

function autoFillPromptFromMapping() {
  if (!selectedCoverageTypeId.value || !selectedDocumentTypeId.value) return;
  const covId = Number(selectedCoverageTypeId.value);
  const docId = Number(selectedDocumentTypeId.value);
  const mapping = promptMappings.value.find((m) => m.coverageTypeId === covId && m.documentTypeId === docId);
  if (mapping) {
    selectedPromptId.value = String(mapping.promptId);
    selectedPromptVersion.value = mapping.promptVersion;
    bulkAutoFilled.value = true;
  } else {
    bulkAutoFilled.value = false;
  }
}

function onCoverageOrDocTypeChange() {
  autoFillPromptFromMapping();
  fetchBulkCounts();
}

function handleBulkApplyClick() {
  if (includeValidated.value) {
    showValidatedConfirmation.value = true;
  } else {
    submitBulkApply();
  }
}

async function submitBulkApply() {
  showValidatedConfirmation.value = false;
  if (!canSubmitBulkApply.value) return;
  bulkApplySubmitting.value = true;
  try {
    await aiService.bulkInvoke({
      promptId: Number(selectedPromptId.value),
      promptVersion: selectedPromptVersion.value,
      coverageTypeId: Number(selectedCoverageTypeId.value),
      documentTypeId: Number(selectedDocumentTypeId.value),
      includeUnprocessed: includeUnprocessed.value,
      includeUnvalidated: includeUnvalidated.value,
      includeExceptions: includeExceptions.value,
      includeValidated: includeValidated.value,
      maxDocuments: maxDocuments.value,
      ...(effectiveDateFrom.value ? { effectiveDateFrom: effectiveDateFrom.value } : {}),
      ...(effectiveDateTo.value ? { effectiveDateTo: effectiveDateTo.value } : {}),
    });

    // Close the modal and return to the list where the new invocations appear
    showBulkApplyModal.value = false;

    // Refresh counts and active invocations. loadActiveInvocations picks the polling
    // strategy: per-invocation polls for small batches, one aggregate refresh loop for
    // large ones (CAC-5607) — no per-invocation poll storm here.
    fetchBulkCounts();
    await loadActiveInvocations();
  } catch (e) {
    console.error('Failed to submit bulk apply', e);
  } finally {
    bulkApplySubmitting.value = false;
  }
}

// Bulk apply watchers
watch([selectedCoverageTypeId, selectedDocumentTypeId], () => {
  onCoverageOrDocTypeChange();
});

watch(selectedPromptId, () => {
  if (!bulkAutoFilled.value) {
    selectedPromptVersion.value = '';
  }
  bulkAutoFilled.value = false;
});

// Debounce the account-name search so the input stays responsive and we only issue a new
// server request once the user pauses typing.
let accountSearchDebounce: ReturnType<typeof setTimeout> | null = null;
watch(accountNameSearch, (val) => {
  if (accountSearchDebounce) clearTimeout(accountSearchDebounce);
  accountSearchDebounce = setTimeout(() => {
    debouncedAccountSearch.value = val;
  }, 300);
});

watch([selectedStatusFilter, debouncedAccountSearch], dropDeepLinkQuery);

// Shared server-side filters (account/prompt/user) apply to both the Completed and Failed
// paginated views. Reset both pages to zero and reload both datasets. Status stays
// client-side and only picks which dataset renders — no reload needed on status change.
watch([debouncedAccountSearch, selectedPromptFilter, selectedUserFilter], () => {
  if (currentPage.value !== 0) currentPage.value = 0;
  else loadResults();

  if (failuresPage.value !== 0) failuresPage.value = 0;
  else loadExtractionFailures();
});

let dateRangeDebounce: ReturnType<typeof setTimeout> | null = null;
watch([effectiveDateFrom, effectiveDateTo], () => {
  if (dateRangeDebounce) clearTimeout(dateRangeDebounce);
  dateRangeDebounce = setTimeout(() => {
    fetchBulkCounts();
  }, 300);
});
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <!-- Header. Hidden while reviewing a run so the document and fields get the height:
         v-if rather than v-show, since the global Tailwind import is `important` and
         display:none loses to the flex utility on this row. -->
    <div
      v-if="!selectedResult"
      class="flex shrink-0 flex-wrap items-center gap-4">
      <div class="flex items-center gap-2 pl-2">
        <History class="h-6 w-6 text-blue-600" />
        <h1 class="text-2xl">Extraction Runs</h1>
        <Badge
          v-if="displayCount > 0"
          variant="secondary">
          {{ displayCount }}
        </Badge>
        <span
          v-if="queuedCount + runningCount > 0"
          class="text-sm text-slate-500">
          {{ queuedCount }} queued · {{ runningCount }} running
        </span>
      </div>
      <AppButton
        class="ml-auto"
        @click="showBulkApplyModal = true">
        <Sparkles class="h-4 w-4" />
        Bulk Apply Prompt
      </AppButton>
    </div>

    <!-- Bulk Apply Prompt modal -->
    <AppModal
      :show="showBulkApplyModal"
      title="Bulk Apply Prompt"
      cancel-button-label="Cancel"
      :action-button-label="`Apply to ${bulkApplyCount} Documents`"
      :handle-cancel="() => (showBulkApplyModal = false)"
      :handle-action="handleBulkApplyClick"
      :action-disabled="!canSubmitBulkApply"
      :loading="bulkApplySubmitting"
      size="lg">
      <template #content>
        <div class="space-y-4">
              <!-- Row 1: Coverage Type + Document Type -->
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-1">
                  <UILabel class="text-xs tracking-wide text-muted-foreground uppercase">Coverage Type</UILabel>
                  <Combobox
                    v-model="selectedCoverageTypeId"
                    :options="coverageTypeOptions"
                    placeholder="Select coverage type"
                    input-class="h-9 text-sm" />
                </div>
                <div class="space-y-1">
                  <UILabel class="text-xs tracking-wide text-muted-foreground uppercase">Document Type</UILabel>
                  <UISelect
                    v-model="selectedDocumentTypeId"
                    :options="documentTypeOptions"
                    placeholder="Select document type"
                    class="h-9" />
                </div>
              </div>

              <!-- Row 2: Prompt + Version -->
              <div class="grid grid-cols-[1fr_140px] gap-4">
                <div class="space-y-1">
                  <UILabel class="text-xs tracking-wide text-muted-foreground uppercase">
                    Prompt
                    <span
                      v-if="bulkAutoFilled"
                      class="ml-1 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary"
                      >Auto-filled from mapping</span
                    >
                  </UILabel>
                  <UISelect
                    v-model="selectedPromptId"
                    :options="promptOptions"
                    placeholder="Select prompt"
                    :class="{ 'border-primary': bulkAutoFilled }"
                    class="h-9" />
                </div>
                <div class="space-y-1">
                  <UILabel class="text-xs tracking-wide text-muted-foreground uppercase">Version</UILabel>
                  <UISelect
                    v-model="selectedPromptVersion"
                    :options="versionOptions"
                    placeholder="Version"
                    :class="{ 'border-primary': bulkAutoFilled }"
                    class="h-9" />
                </div>
              </div>

              <!-- Row 2.5: Policy Effective Date Range (optional) -->
              <div
                class="rounded-md border p-3"
                :class="isDateRangeInverted ? 'border-warning bg-warning/5' : 'border-primary/30 bg-primary/5'">
                <UILabel class="mb-2 block text-xs tracking-wide text-muted-foreground uppercase">
                  Policy Effective Date
                </UILabel>
                <div class="grid grid-cols-2 gap-4">
                  <DatePickerInput
                    v-model="effectiveDateFrom"
                    label="From"
                    placeholder="mm/dd/yyyy"
                    :class="isDateRangeInverted ? 'border-warning' : ''" />
                  <DatePickerInput
                    v-model="effectiveDateTo"
                    label="To"
                    placeholder="mm/dd/yyyy"
                    :class="isDateRangeInverted ? 'border-warning' : ''" />
                </div>
                <p
                  v-if="isDateRangeInverted"
                  class="mt-2 text-xs text-warning">
                  End date is before start date.
                </p>
                <p
                  v-else
                  class="mt-2 text-xs text-muted-foreground">
                  Optional. Inclusive on both ends. Documents with no effective date are excluded when either bound is set.
                </p>
              </div>

              <!-- Row 3: Document Status Checkboxes -->
              <div>
                <UILabel class="mb-2 block text-xs tracking-wide text-muted-foreground uppercase">Documents to Include</UILabel>
                <div class="flex flex-wrap gap-3">
                  <label class="flex cursor-pointer items-center gap-2 rounded-md border border-base-300 bg-base-200 px-3 py-2">
                    <input
                      type="checkbox"
                      v-model="includeUnprocessed"
                      class="checkbox checkbox-sm checkbox-primary" />
                    <span class="text-sm">Unprocessed</span>
                    <span
                      v-if="bulkCounts"
                      class="badge badge-sm badge-info"
                      >{{ bulkCounts.unprocessed }}</span
                    >
                  </label>
                  <label class="flex cursor-pointer items-center gap-2 rounded-md border border-base-300 bg-base-200 px-3 py-2">
                    <input
                      type="checkbox"
                      v-model="includeUnvalidated"
                      class="checkbox checkbox-sm checkbox-primary" />
                    <span class="text-sm">Unvalidated</span>
                    <span
                      v-if="bulkCounts"
                      class="badge badge-sm badge-warning"
                      >{{ bulkCounts.unvalidated }}</span
                    >
                  </label>
                  <label class="flex cursor-pointer items-center gap-2 rounded-md border border-base-300 bg-base-200 px-3 py-2">
                    <input
                      type="checkbox"
                      v-model="includeExceptions"
                      class="checkbox checkbox-sm checkbox-primary" />
                    <span class="text-sm">Exceptions</span>
                    <span
                      v-if="bulkCounts"
                      class="badge badge-sm badge-error"
                      >{{ bulkCounts.exceptions }}</span
                    >
                  </label>
                  <label class="flex cursor-pointer items-center gap-2 rounded-md border border-base-300 bg-base-200 px-3 py-2">
                    <input
                      type="checkbox"
                      v-model="includeValidated"
                      class="checkbox checkbox-sm checkbox-primary" />
                    <span class="text-sm">Validated</span>
                    <span
                      v-if="bulkCounts"
                      class="badge badge-sm badge-success"
                      >{{ bulkCounts.validated }}</span
                    >
                  </label>
                </div>
              </div>

              <!-- Row 4: Max Documents -->
              <div class="w-56 space-y-2">
                <UILabel class="text-xs tracking-wide text-muted-foreground uppercase">Max Documents to Process</UILabel>
                <UIInput
                  type="number"
                  :model-value="String(maxDocuments)"
                  @update:model-value="maxDocuments = Number($event) || 1"
                  min="1"
                  max="100"
                  class="h-9" />
              </div>

              <!-- Summary line -->
              <div
                v-if="bulkCounts && selectedBucketTotal > 0"
                class="rounded bg-primary/5 px-3 py-2 text-xs text-primary">
                {{ selectedBucketTotal }} documents match your filters. Processing first {{ bulkApplyCount }}.
              </div>
        </div>
      </template>
    </AppModal>

    <!-- Results List Card (list view only) -->
    <div
      v-show="!selectedResult"
      class="shrink-0">
      <Card class="relative border-slate-200 bg-white/80 backdrop-blur-sm">
        <CardHeader class="pb-2">
            <!-- Search + filters, inline -->
            <div class="flex flex-wrap items-center gap-4 px-2">
              <div
                v-if="hasAnyResults || hasActiveFilters"
                class="relative w-72 shrink-0">
                <Search class="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <UIInput
                  v-model="accountNameSearch"
                  type="text"
                  placeholder="Search by account name"
                  class="h-10 border border-slate-400 pl-9" />
              </div>

              <!-- Filters -->
              <div
                v-if="hasAnyResults || hasActiveFilters"
                class="ml-auto flex flex-wrap items-center gap-3"
                @click.stop>
                <div class="flex items-center gap-2">
                  <label class="text-sm font-medium text-slate-600">Status:</label>
                  <UISelect
                    v-model="selectedStatusFilter"
                    :options="statusFilterOptions"
                    placeholder="All"
                    class="w-[140px]" />
                </div>
                <div class="flex items-center gap-2">
                  <label class="text-sm font-medium text-slate-600">Prompt:</label>
                  <UISelect
                    v-model="selectedPromptFilter"
                    :options="uniquePrompts"
                    placeholder="All Prompts"
                    class="w-[180px]" />
                </div>
                <div class="flex items-center gap-2">
                  <label class="text-sm font-medium text-slate-600">User:</label>
                  <UISelect
                    v-model="selectedUserFilter"
                    :options="uniqueUsers"
                    placeholder="All Users"
                    class="w-[200px]" />
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <!-- Loading State (only on initial/empty load — pagination keeps stale rows during load) -->
            <div
              v-if="isLoading && sortedResults.length === 0"
              class="flex items-center justify-center py-8">
              <Loader class="mr-2 h-6 w-6 animate-spin text-blue-600" />
              <span class="text-slate-600">Loading results...</span>
            </div>

            <!-- Error State -->
            <div
              v-else-if="error"
              class="flex items-center justify-center py-8">
              <AlertCircle class="mr-2 h-6 w-6 text-red-500" />
              <span class="text-red-600">{{ error }}</span>
            </div>

            <!-- Empty State -->
            <div
              v-else-if="sortedResults.length === 0"
              class="py-12 text-center">
              <Clock class="mx-auto mb-4 h-12 w-12 text-slate-300" />
              <h3 class="mb-2 text-lg font-medium text-slate-700">No Results Yet</h3>
              <p class="mx-auto max-w-sm text-sm text-slate-500">Execute prompts to see your execution history here.</p>
            </div>

            <!-- Results List -->
            <div
                v-if="sortedResults.length > 0"
                ref="resultsListEl"
                class="max-h-[60vh] space-y-2 overflow-y-auto">
                <div
                  v-for="item in sortedResults"
                  :key="getItemKey(item)"
                  :data-linked-row="isLinkedFailure(item) || undefined"
                  :class="[
                    'flex items-center justify-between rounded-lg border p-3 transition-all',
                    isLastViewed(item) || isLinkedFailure(item)
                      ? 'border-blue-500 bg-blue-50 shadow-sm'
                      : isExtractionFailure(item)
                        ? 'border-red-200 bg-red-50'
                        : isInvocation(item) && (item.status === 'PENDING' || item.status === 'RUNNING')
                          ? 'border-slate-200 bg-slate-50 opacity-75'
                          : isInvocation(item) && item.status === 'FAILED'
                            ? 'border-red-200 bg-red-50'
                            : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50',
                  ]">
                  <div class="flex flex-1 flex-wrap items-center gap-4">
                    <!-- Status Icon (for extraction failures) -->
                    <div
                      v-if="isExtractionFailure(item)"
                      class="flex items-center gap-1">
                      <AlertCircle class="h-4 w-4 text-red-600" />
                    </div>

                    <!-- Status Icon (for invocations) -->
                    <div
                      v-else-if="isInvocation(item)"
                      class="flex items-center gap-1">
                      <Loader
                        v-if="item.status === 'RUNNING'"
                        class="h-4 w-4 animate-spin text-blue-600" />
                      <Clock
                        v-else-if="item.status === 'PENDING'"
                        class="h-4 w-4 animate-pulse text-yellow-600" />
                      <CheckCircle2
                        v-else-if="item.status === 'COMPLETED'"
                        class="h-4 w-4 text-green-600" />
                      <AlertCircle
                        v-else-if="item.status === 'FAILED'"
                        class="h-4 w-4 text-red-600" />
                    </div>

                    <!-- Created At -->
                    <div class="flex items-center gap-1 text-sm text-slate-500">
                      <Clock
                        v-if="!isInvocation(item) || item.status !== 'RUNNING'"
                        class="h-4 w-4" />
                      <span>{{ formatDate(item.createdAt) }}</span>
                    </div>

                    <!-- Status Badge (for extraction failures) -->
                    <div
                      v-if="isExtractionFailure(item)"
                      class="flex items-center gap-1">
                      <Badge
                        variant="destructive"
                        class="rounded-md px-2 py-1 text-xs">
                        {{ item.status }}
                      </Badge>
                      <Badge
                        variant="outline"
                        class="rounded-md px-2 py-1 text-xs text-slate-600">
                        {{
                          item.sourceTrigger === 'EPIC_ETL'
                            ? 'Epic ETL'
                            : item.sourceTrigger === 'INTERNAL_UPLOAD'
                              ? 'Upload'
                              : item.sourceTrigger === 'BULK'
                                ? 'Bulk'
                                : 'Manual'
                        }}
                      </Badge>
                    </div>

                    <!-- Status Badge (for invocations) -->
                    <div
                      v-else-if="isInvocation(item)"
                      class="flex items-center gap-1">
                      <Badge
                        :variant="getStatusVariant(item.status)"
                        :class="`rounded-md px-2 py-1 text-xs ${getStatusBadgeClass(item.status)}`">
                        {{ getStatusLabel(item.status) }}
                      </Badge>
                    </div>

                    <!-- Model -->
                    <div
                      v-if="!isExtractionFailure(item)"
                      class="flex min-w-[50px] items-center gap-1">
                      <BotIcon class="h-4 w-4 text-blue-500" />
                      <Badge
                        variant="outline"
                        class="rounded-md px-2 py-1 text-xs">
                        {{ getModelDisplayName(item.modelArn) }}
                      </Badge>
                    </div>

                    <!-- Account -->
                    <div
                      v-if="isPromptResult(item) && getAccountChipInfo(item)"
                      class="flex min-w-[50px] items-center gap-1">
                      <Building2 class="h-4 w-4 text-indigo-500" />
                      <Badge
                        variant="outline"
                        class="cursor-pointer rounded-md px-2 py-1 text-xs text-indigo-700 hover:bg-indigo-50"
                        @click="goToAccountPolicies(item, $event)">
                        {{ getAccountChipInfo(item)!.accountName }}
                      </Badge>
                    </div>

                    <!-- Prompt Name -->
                    <div
                      v-if="isPromptResult(item) && item.promptName"
                      class="flex min-w-[50px] items-center gap-1">
                      <Sparkles class="h-4 w-4 text-amber-500" />
                      <Badge
                        variant="outline"
                        class="cursor-pointer rounded-md px-2 py-1 text-xs text-amber-700 hover:bg-amber-50"
                        @click="navigateToPrompt(item.promptName!, $event)">
                        {{ item.promptName }}
                      </Badge>
                    </div>

                    <!-- Document -->
                    <div class="flex min-w-[50px] items-center gap-1">
                      <FileText class="h-4 w-4 text-green-500" />
                      <Badge
                        variant="outline"
                        class="rounded-md px-2 py-1 text-xs">
                        {{ item.documentName || 'No document' }}
                      </Badge>
                    </div>

                    <!-- External upload: this extraction creates a policy rather than
                         overriding one, at any stage of the run. Internal validation work
                         carries no badge. -->
                    <Badge
                      v-if="isExternalResult(item)"
                      variant="secondary"
                      class="rounded-md border-amber-300 bg-amber-100 px-2 py-1 text-xs text-amber-800"
                      data-testid="external-designation-badge">
                      External
                    </Badge>

                    <!-- User -->
                    <div class="flex min-w-[50px] items-center gap-1">
                      <User class="h-4 w-4 text-purple-500" />
                      <Badge
                        variant="outline"
                        class="rounded-md px-2 py-1 text-xs">
                        {{ item.userEmail }}
                      </Badge>
                    </div>

                    <!-- Error Message (for failed invocations) -->
                    <div
                      v-if="(isInvocation(item) && item.status === 'FAILED' && item.errorMessage) || (isExtractionFailure(item) && item.errorMessage)"
                      class="flex items-center gap-1 text-xs text-red-600">
                      <AlertCircle class="h-3 w-3" />
                      <span class="max-w-xs truncate">{{ item.errorMessage }}</span>
                    </div>

                    <!-- Right-aligned section: Review Status + Validation Badge + Reprocess -->
                    <div
                      v-if="isPromptResult(item)"
                      class="ml-auto flex items-center gap-2">
                      <!-- Review Status -->
                      <div
                        v-if="item.reviewCount > 0"
                        class="flex items-center gap-1">
                        <CheckCircle2 class="h-4 w-4 text-green-600" />
                        <span class="text-xs text-green-700"> Reviewed by {{ item.reviews[0]?.userEmail }} </span>
                        <span class="text-xs text-slate-400">
                          {{ formatReviewDate(item.reviews[0]?.reviewedAt) }}
                        </span>
                        <Badge
                          v-if="item.reviewCount > 1"
                          variant="outline"
                          class="rounded-full px-1.5 py-0 text-[10px]">
                          +{{ item.reviewCount - 1 }}
                        </Badge>
                      </div>

                      <!-- Validation Status Badge -->
                      <Badge
                        v-if="!item.isMalformed"
                        variant="outline"
                        class="flex items-center gap-1 rounded-md border-green-200 bg-green-50 px-2 py-1 text-xs text-green-700">
                        <CheckCircle2 class="h-3 w-3" />
                        Valid
                      </Badge>
                      <Badge
                        v-else
                        variant="outline"
                        class="flex items-center gap-1 rounded-md border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700">
                        <AlertTriangle class="h-3 w-3" />
                        Malformed
                      </Badge>

                      <!-- Reprocess -->
                      <Tooltip
                        text="Reprocess Document"
                        position="top">
                        <button
                          type="button"
                          class="btn p-1 text-slate-600 btn-ghost btn-xs hover:bg-slate-100"
                          @click.stop="handleReprocess(item, $event)">
                          <RotateCw class="h-3.5 w-3.5" />
                        </button>
                      </Tooltip>
                    </div>
                  </div>

                  <!-- View action: full-height edge affordance that opens the detail page -->
                  <button
                    v-if="canOpenResult(item)"
                    type="button"
                    aria-label="View details"
                    title="View details"
                    class="group -my-3 -mr-3 ml-3 flex shrink-0 items-center self-stretch rounded-l-none rounded-r-lg border-l border-slate-200 px-4 text-slate-400 transition-colors duration-200 hover:bg-primary/10 hover:text-primary"
                    @click="openResult(item)">
                    <ArrowRight class="h-4 w-4" />
                  </button>
                </div>

              </div>

              <!-- Pagination (completed results history) -->
              <div
                v-if="showPagination && (hasPrevPage || hasNextPage)"
                class="flex gap-4 pt-4">
                <AppButton
                  secondary
                  :disabled="!hasPrevPage || isLoading"
                  @click="goToPreviousPage"
                  data-test="prev-button">
                  <CircleChevronLeft
                    :size="20"
                    class="mr-2 text-primary" />
                  Previous
                </AppButton>
                <AppButton
                  secondary
                  :disabled="!hasNextPage || isLoading"
                  @click="goToNextPage"
                  data-test="next-button">
                  Next
                  <CircleChevronRight
                    :size="20"
                    class="ml-2 text-primary" />
                </AppButton>
              </div>
          </CardContent>
      </Card>
    </div>

    <!-- Detail View: back navigation + metadata header + document/fields -->
    <div
      v-if="selectedResult"
      class="flex flex-col gap-3">
      <!-- Back navigation -->
      <div class="flex shrink-0 items-center">
        <button
          type="button"
          class="btn gap-1 btn-sm"
          @click="handleBackToList">
          <ArrowLeft class="h-4 w-4" />
          Back to results
        </button>
      </div>

      <!-- Metadata header for the opened result -->
      <div class="shrink-0 rounded-lg border border-blue-500 bg-blue-50 p-3 shadow-sm">
        <div class="flex flex-wrap items-center gap-4">
          <!-- Created At -->
          <div class="flex items-center gap-1 text-sm text-slate-500">
            <Clock class="h-4 w-4" />
            <span>{{ formatDate(selectedResult.createdAt) }}</span>
          </div>

          <!-- Model -->
          <div class="flex items-center gap-1">
            <BotIcon class="h-4 w-4 text-blue-500" />
            <Badge
              variant="outline"
              class="rounded-md px-2 py-1 text-xs">
              {{ getModelDisplayName(selectedResult.modelArn) }}
            </Badge>
          </div>

          <!-- Account -->
          <div
            v-if="getAccountChipInfo(selectedResult)"
            class="flex items-center gap-1">
            <Building2 class="h-4 w-4 text-indigo-500" />
            <Badge
              variant="outline"
              class="cursor-pointer rounded-md px-2 py-1 text-xs text-indigo-700 hover:bg-indigo-50"
              @click="goToAccountPolicies(selectedResult, $event)">
              {{ getAccountChipInfo(selectedResult)!.accountName }}
            </Badge>
          </div>

          <!-- Prompt Name -->
          <div
            v-if="selectedResult.promptName"
            class="flex items-center gap-1">
            <Sparkles class="h-4 w-4 text-amber-500" />
            <Badge
              variant="outline"
              class="cursor-pointer rounded-md px-2 py-1 text-xs text-amber-700 hover:bg-amber-50"
              @click="navigateToPrompt(selectedResult.promptName!, $event)">
              {{ selectedResult.promptName }}
            </Badge>
          </div>

          <!-- Document -->
          <div class="flex items-center gap-1">
            <FileText class="h-4 w-4 text-green-500" />
            <Badge
              variant="outline"
              class="rounded-md px-2 py-1 text-xs">
              {{ selectedResult.documentName || 'No document' }}
            </Badge>
          </div>

          <Badge
            v-if="isExternalResult(selectedResult)"
            variant="secondary"
            class="rounded-md border-amber-300 bg-amber-100 px-2 py-1 text-xs text-amber-800"
            data-testid="external-designation-badge">
            External
          </Badge>

          <!-- User -->
          <div class="flex items-center gap-1">
            <User class="h-4 w-4 text-purple-500" />
            <Badge
              variant="outline"
              class="rounded-md px-2 py-1 text-xs">
              {{ selectedResult.userEmail }}
            </Badge>
          </div>

          <!-- Right-aligned: Review Status + Validation Badge + Reprocess -->
          <div class="ml-auto flex items-center gap-3">
            <!-- Review Status -->
            <div
              v-if="selectedResult.reviewCount > 0"
              class="flex items-center gap-1">
              <CheckCircle2 class="h-4 w-4 text-green-600" />
              <span class="text-xs text-green-700"> Reviewed by {{ selectedResult.reviews[0]?.userEmail }} </span>
              <span class="text-xs text-slate-400">
                {{ formatReviewDate(selectedResult.reviews[0]?.reviewedAt) }}
              </span>
              <Badge
                v-if="selectedResult.reviewCount > 1"
                variant="outline"
                class="rounded-full px-1.5 py-0 text-[10px]">
                +{{ selectedResult.reviewCount - 1 }}
              </Badge>
            </div>

            <!-- Validation Status Badge -->
            <Badge
              v-if="!selectedResult.isMalformed"
              variant="outline"
              class="flex items-center gap-1 rounded-md border-green-200 bg-green-50 px-2 py-1 text-xs text-green-700">
              <CheckCircle2 class="h-3 w-3" />
              Valid
            </Badge>
            <Badge
              v-else
              variant="outline"
              class="flex items-center gap-1 rounded-md border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700">
              <AlertTriangle class="h-3 w-3" />
              Malformed
            </Badge>

            <!-- Reprocess -->
            <Tooltip
              text="Reprocess Document"
              position="left">
              <button
                type="button"
                class="btn p-1 text-slate-600 btn-ghost btn-xs hover:bg-slate-100"
                @click="handleReprocess(selectedResult, $event)">
                <RotateCw class="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      <!-- Document viewer and extracted fields -->
      <ExtractionReviewShell
        :result="selectedResult"
        class="h-[calc(100vh-300px)] max-h-[1000px] min-h-[420px]"
        @review-completed="handleReviewCompleted">
        <template #action-panel="{ target, entityMatches, entityValues }">
          <div
            v-if="target?.kind === 'new_external_policy'"
            class="shrink-0 rounded-lg border border-slate-200 bg-white/80 p-3 backdrop-blur-sm">
            <ExternalPolicyTargetSummary
              :target="target"
              :entity-matches="entityMatches"
              :entity-values="entityValues"
              show-review-link />
          </div>
        </template>
      </ExtractionReviewShell>
    </div>

    <!-- Reprocess Modal -->
    <ReprocessModal
      ref="reprocessModalRef"
      @reprocessed="handleReprocessed" />

    <!-- Validated Confirmation Dialog -->
    <div
      v-if="showValidatedConfirmation"
      class="modal-open modal">
      <div class="modal-box">
        <h3 class="text-lg font-bold">Reprocess Validated Documents?</h3>
        <p class="py-4">
          You've selected documents with already-validated extraction results. Reprocessing will create new extraction results that may differ from
          the validated ones. Continue?
        </p>
        <div class="modal-action">
          <button
            class="btn btn-ghost"
            @click="showValidatedConfirmation = false">
            Cancel
          </button>
          <button
            class="btn btn-warning"
            @click="submitBulkApply">
            Continue
          </button>
        </div>
      </div>
      <div
        class="modal-backdrop"
        @click="showValidatedConfirmation = false"></div>
    </div>
  </div>
</template>
